import { ArrowRight, Calendar, MagnifyingGlass, MapTrifold, Handshake } from "@phosphor-icons/react";
import { openStrategySession } from "@/lib/calcom";

const AGENDA = [
  {
    n: "01",
    accent: "#00F0FF",
    icon: MagnifyingGlass,
    title: "Where you are today",
    desc: "Your current GTM stack, team structure, and the specific pipeline pain you're feeling.",
  },
  {
    n: "02",
    accent: "#FFB800",
    icon: MapTrifold,
    title: "The highest-leverage systems",
    desc: "We walk through the 2–3 systems in our 8-system stack that will move your numbers fastest.",
  },
  {
    n: "03",
    accent: "#CCFF00",
    icon: Handshake,
    title: "Is this a fit — or not",
    desc: "Straight answer on whether we're the right partner right now. If we're not, we'll tell you who is.",
  },
];

export default function CTASection() {
  return (
    <section
      id="cta"
      data-testid="cta-section"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="absolute inset-0 precision-grid opacity-30 pointer-events-none" />
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(204,255,0,0.18) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-start">
          {/* Left */}
          <div className="col-span-12 lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#CCFF00]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
                Ready When You Are / 09
              </span>
            </div>

            <h2 className="font-display font-bold uppercase tracking-[-0.04em] leading-[0.9] text-white text-[44px] sm:text-[64px] md:text-[80px] lg:text-[96px]">
              Scale your
              <br />
              pipeline.
              <br />
              <span className="text-[#CCFF00]">Not your payroll.</span>
            </h2>

            <p className="mt-8 font-body text-zinc-300 text-[15px] md:text-[17px] leading-relaxed max-w-[54ch]">
              Skip the guesswork. A 45-minute strategy session — we map your
              current stack, identify the 3 highest-leverage systems, and tell
              you straight whether now is the right time to build.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={openStrategySession}
                data-testid="cta-book-session-button"
                className="inline-flex items-center gap-3 bg-[#CCFF00] text-black font-mono font-bold uppercase tracking-[0.15em] text-[12px] px-7 py-5 hover:bg-white transition-colors"
              >
                <Calendar size={16} weight="bold" />
                Book Strategy Session
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>

            {/* Risk reversal */}
            <div className="mt-12 glass p-6 relative max-w-[540px]" data-testid="risk-reversal">
              <div className="absolute -top-2 -left-2 w-3 h-3 border-l border-t border-[#D946EF]" />
              <div className="absolute -top-2 -right-2 w-3 h-3 border-r border-t border-[#D946EF]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D946EF] mb-3">
                Risk Reversal · Guaranteed
              </div>
              <p className="font-display font-bold text-white text-xl md:text-2xl uppercase tracking-tighter leading-tight">
                If we don&apos;t deliver qualified
                <br />
                pipeline — <span className="text-[#D946EF]">you don&apos;t pay.</span>
              </p>
              <p className="mt-3 font-body text-[13px] text-zinc-400">
                Performance-aligned. Contract-backed. Measured against SQLs booked
                into your CRM — not slide decks.
              </p>
            </div>
          </div>

          {/* Right — session agenda */}
          <div className="col-span-12 lg:col-span-6">
            <div
              className="border border-white/15 bg-black/40 backdrop-blur-xl p-8 md:p-10 relative"
              data-testid="session-agenda"
            >
              <div className="absolute -top-2 -left-2 w-3 h-3 border-l border-t border-[#00F0FF]" />
              <div className="absolute -top-2 -right-2 w-3 h-3 border-r border-t border-[#00F0FF]" />
              <div className="absolute -bottom-2 -left-2 w-3 h-3 border-l border-b border-[#00F0FF]" />
              <div className="absolute -bottom-2 -right-2 w-3 h-3 border-r border-b border-[#00F0FF]" />

              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                // session.agenda
              </div>
              <div className="font-display font-bold text-white text-2xl md:text-[28px] uppercase tracking-tighter leading-[0.95] mb-8">
                What happens<br />on the call.
              </div>

              <div className="space-y-7">
                {AGENDA.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.n} className="flex items-start gap-5">
                      <div
                        className="h-11 w-11 border flex items-center justify-center shrink-0"
                        style={{ borderColor: item.accent + "55", color: item.accent }}
                      >
                        <Icon size={18} weight="duotone" />
                      </div>
                      <div className="flex-1 border-b border-dashed border-white/10 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-baseline gap-3 mb-1">
                          <span
                            className="font-mono text-[10px] font-bold tabular-nums"
                            style={{ color: item.accent }}
                          >
                            {item.n}
                          </span>
                          <span className="font-display font-bold uppercase tracking-tighter text-white text-base md:text-lg">
                            {item.title}
                          </span>
                        </div>
                        <div className="font-body text-zinc-400 text-[13px] md:text-[14px] leading-relaxed">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 pt-6 border-t border-dashed border-white/10">
                <button
                  type="button"
                  onClick={openStrategySession}
                  data-testid="cta-book-session-button-bottom"
                  className="w-full bg-[#CCFF00] text-black font-mono font-bold py-4 flex items-center justify-center gap-3 uppercase tracking-[0.15em] text-[12px] hover:bg-white transition-colors"
                >
                  <Calendar size={16} weight="bold" />
                  Pick A Time
                  <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
